import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Monitor, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { BookingPageBlock } from '@/pages/public/booking-page/types';

// The real public page in an iframe, fed the draft booking_page block over
// postMessage (see BookingProfilePage preview mode). An iframe keeps the
// page's own mobile/desktop breakpoints, which a scaled component can't.
const SIZES = { mobile: { width: 390, height: 780 }, desktop: { width: 1280, height: 900 } } as const;

interface PreviewPanelProps {
  subdomain: string;
  block: BookingPageBlock;
  publicUrl: string;
  width: number; // available width in px
  mode?: 'mobile' | 'desktop';
  showToggle?: boolean;
  className?: string;
}

export function PreviewFrame({ subdomain, block, width, mode }: { subdomain: string; block: BookingPageBlock; width: number; mode: 'mobile' | 'desktop' }) {
  const frame = useRef<HTMLIFrameElement>(null);
  // Bumped each time the framed page says it's ready (first load, mode switch).
  const [ready, setReady] = useState(0);
  const size = SIZES[mode];
  const scale = Math.min(1, width / size.width);
  const src = `${window.location.origin}/${subdomain}/embed?header=1&photos=1&sidebar=1&preview=1`;

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || event.source !== frame.current?.contentWindow) return;
      if (event.data?.type === 'sloty:preview-ready') setReady((n) => n + 1);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  useEffect(() => {
    if (!ready) return;
    frame.current?.contentWindow?.postMessage({ type: 'sloty:preview', booking_page: block }, window.location.origin);
  }, [ready, block]);

  return (
    <div
      className="relative mx-auto overflow-hidden rounded-[22px] border border-line-strong bg-bg-public"
      style={{ width: size.width * scale, height: size.height * scale }}
    >
      <iframe
        ref={frame}
        key={mode}
        title="Booking page preview"
        src={src}
        style={{ width: size.width, height: size.height, transform: `scale(${scale})`, transformOrigin: 'top left', border: 0 }}
        data-testid="preview-frame"
        data-mode={mode}
      />
    </div>
  );
}

export function PreviewPanel({ subdomain, block, publicUrl, width, mode: forced, showToggle = true, className }: PreviewPanelProps) {
  const { t } = useTranslation('adminBookingPage');
  const [mode, setMode] = useState<'mobile' | 'desktop'>(forced ?? 'mobile');
  const active = forced ?? mode;

  const shows = [
    block.layout === 'gallery' ? t('shows.gallery', { count: block.photos.length }) : block.layout === 'cover' ? t('shows.cover') : t('shows.none'),
    block.hours && t('shows.hours'),
    block.location && t('shows.address'),
    block.location?.latitude != null && t('shows.map'),
    block.show_team ? t('shows.team', { count: block.team.length }) : block.team.length === 1 && t('shows.about', { name: block.team[0].name.split(' ')[0] }),
    block.show_online && t('shows.online'),
  ].filter(Boolean) as string[];

  return (
    <aside className={cn('flex flex-col gap-4 rounded-2xl border border-line bg-surface p-[18px] shadow-card', className)} data-testid="preview-panel">
      {showToggle && (
        <div className="flex items-center justify-between gap-2">
          <h2 className="font-display text-[16px] font-semibold text-ink">{t('preview.title')}</h2>
          <div role="radiogroup" aria-label={t('preview.title')} className="inline-flex gap-0.5 rounded-lg bg-surface-2 p-[3px]">
            {(['mobile', 'desktop'] as const).map((m) => (
              <button
                key={m}
                type="button"
                role="radio"
                aria-checked={active === m}
                onClick={() => setMode(m)}
                className={cn('inline-flex h-8 items-center gap-1.5 rounded-sm px-3 text-[13px]', active === m ? 'bg-surface font-semibold text-ink shadow-sm' : 'font-medium text-soft')}
              >
                {m === 'mobile' ? <Smartphone className="h-3.5 w-3.5" /> : <Monitor className="h-3.5 w-3.5" />}
                {t(`preview.${m}`)}
              </button>
            ))}
          </div>
        </div>
      )}
      <PreviewFrame subdomain={subdomain} block={block} width={width} mode={active} />
      <div className="flex flex-col gap-2" data-testid="preview-shows">
        <span className="text-mono-eyebrow uppercase text-soft">{t('preview.showsTitle')}</span>
        <div className="flex flex-wrap gap-1.5">
          {shows.map((label) => (
            <span key={label} className="inline-flex h-7 items-center rounded-pill bg-brand-50 px-3 text-[13px] font-medium text-brand-700">{label}</span>
          ))}
        </div>
      </div>
      <Button asChild variant="outline" className="h-11">
        <a href={publicUrl} target="_blank" rel="noreferrer"><ExternalLink className="h-4 w-4" />{t('preview.openLive')}</a>
      </Button>
    </aside>
  );
}
